/**
 * Agent Composer page — LangGraph-style visual workflow builder.
 * Routes: /composer (new) and /composer/:id (edit existing WorkflowDefinition).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import { LayoutGrid, Save, ArrowLeft, Link2 } from 'lucide-react';
import { Button, CoordioDropdown } from '@coordio/ui';
import { LoadingSkeleton } from '@coordio/ui/layout';
import { ComposerPalette } from '../components/composer/ComposerPalette';
import { ComposerCanvas } from '../components/composer/ComposerCanvas';
import { ComposerInspector } from '../components/composer/ComposerInspector';
import { ComposerRunPanel } from '../components/composer/ComposerRunPanel';
import { useComposerGraph } from '../components/composer/useComposerGraph';
import { useWorkflowExecutionStream } from '../components/composer/useWorkflowExecutionStream';
import '../components/composer/composer.css';
import { workflowService } from '../services/workflowService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';
import { workflowRequiresInput } from '../utils/workflowInput';
import type { ComposerNodeType, ComposerNodeData, NodeRunStatus } from '../types/composer';
import type { ComposerNode } from '../components/composer/useComposerGraph';

export const AgentComposerPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const graph = useComposerGraph();
  const stream = useWorkflowExecutionStream();

  const [name, setName] = useState('Untitled workflow');
  const [description, setDescription] = useState('');
  const [workflowId, setWorkflowId] = useState<string | null>(id ?? null);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectFrom, setConnectFrom] = useState('');
  const [connectTo, setConnectTo] = useState('');

  // Load existing workflow when editing.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const workflow = await workflowService.getWorkflow(id);
        if (cancelled) return;
        setName(workflow.name);
        setDescription(workflow.description ?? '');
        setWorkflowId(workflow.id);
        graph.loadFromJson(workflow.workflowJson);
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Failed to load workflow';
        addNotification(msg, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Overlay live run statuses onto node data so the status rings light up.
  const nodesWithStatus = useMemo(
    () =>
      graph.nodes.map((n) => {
        const status: NodeRunStatus | undefined = stream.nodeStatuses[n.id];
        if (!status || status === n.data.runStatus) return n;
        return { ...n, data: { ...n.data, runStatus: status } } satisfies ComposerNode;
      }),
    [graph.nodes, stream.nodeStatuses],
  );

  const selectedNode = useMemo(
    () => nodesWithStatus.find((n) => n.id === selectedNodeId) ?? null,
    [nodesWithStatus, selectedNodeId],
  );
  const selectedEdge = useMemo(
    () => graph.edges.find((e) => e.id === selectedEdgeId) ?? null,
    [graph.edges, selectedEdgeId],
  );

  // The run input is required whenever any node template references {{input}}.
  const requiresInput = useMemo(
    () => workflowRequiresInput(JSON.stringify(graph.nodes.map((n) => n.data))),
    [graph.nodes],
  );

  const handleAddNodeAtCenter = useCallback(
    (type: ComposerNodeType) => {
      // Keyboard / click fallback — drop near the center of the viewport.
      const x = 180 + graph.nodes.length * 40;
      const y = 160 + (graph.nodes.length % 3) * 40;
      const nodeId = graph.addNode(type, { x, y });
      setSelectedNodeId(nodeId);
      setSelectedEdgeId(null);
    },
    [graph],
  );

  const handleAddNodeAt = useCallback(
    (type: ComposerNodeType, position: { x: number; y: number }) => {
      const nodeId = graph.addNode(type, position);
      setSelectedNodeId(nodeId);
      setSelectedEdgeId(null);
    },
    [graph],
  );

  const handleUpdateNode = useCallback(
    (nodeId: string, patch: Partial<ComposerNodeData>) => {
      graph.updateNodeData(nodeId, patch);
    },
    [graph],
  );

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setValidationErrors(['Workflow name is required.']);
      return;
    }

    const workflowJson = graph.toJson();
    setSaving(true);
    setValidationErrors([]);

    try {
      // Client-side validate first so the user sees named errors without a round-trip.
      const validation = await workflowService.validateGraph(workflowJson);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        addNotification('Workflow graph is invalid — fix the highlighted issues before saving.', 'error');
        setSaving(false);
        return;
      }

      if (workflowId) {
        await workflowService.updateWorkflow(workflowId, {
          name: name.trim(),
          description: description.trim() || undefined,
          workflowJson,
          isActive: true,
          version: '1.0.0',
        });
        addNotification('Workflow saved', 'success');
      } else {
        const created = await workflowService.createWorkflow({
          name: name.trim(),
          description: description.trim() || undefined,
          workflowJson,
          workflowType: 'Custom',
          patternType: 'None',
          supportsCheckpointing: true,
          supportsStreaming: true,
          version: '1.0.0',
        });
        setWorkflowId(created.id);
        addNotification('Workflow created', 'success');
        navigate(`/composer/${created.id}`, { replace: true });
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Failed to save workflow';
      setValidationErrors([msg]);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  }, [addNotification, description, graph, name, navigate, workflowId]);

  const handleRun = useCallback(
    async (input: string) => {
      if (!name.trim()) {
        setValidationErrors(['Workflow name is required.']);
        return;
      }

      const workflowJson = graph.toJson();

      // Persist (create or update) so the engine always sees the latest graph.
      let idToRun = workflowId;
      try {
        const validation = await workflowService.validateGraph(workflowJson);
        if (!validation.isValid) {
          setValidationErrors(validation.errors);
          addNotification('Fix validation errors before running.', 'error');
          return;
        }

        if (idToRun) {
          await workflowService.updateWorkflow(idToRun, {
            name: name.trim(),
            description: description.trim() || undefined,
            workflowJson,
            isActive: true,
            version: '1.0.0',
          });
        } else {
          const created = await workflowService.createWorkflow({
            name: name.trim(),
            description: description.trim() || undefined,
            workflowJson,
            workflowType: 'Custom',
            patternType: 'None',
            supportsCheckpointing: true,
            supportsStreaming: true,
            version: '1.0.0',
          });
          idToRun = created.id;
          setWorkflowId(created.id);
          navigate(`/composer/${created.id}`, { replace: true });
        }
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Failed to save before run';
        setValidationErrors([msg]);
        addNotification(msg, 'error');
        return;
      }

      try {
        const response = await workflowService.executeWorkflow(idToRun, {
          parameters: { input },
        });
        await stream.start(response.executionId);
        addNotification(`Execution started · ${response.executionId.slice(0, 8)}…`, 'success');
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Failed to execute workflow';
        addNotification(msg, 'error');
      }
    },
    [addNotification, description, graph, name, navigate, stream, workflowId],
  );

  if (loading) {
    return (
      <div className="cx-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSkeleton rows={3} />
      </div>
    );
  }

  const handleConnectDialogSubmit = () => {
    if (!connectFrom || !connectTo || connectFrom === connectTo) return;
    graph.onConnect({ source: connectFrom, target: connectTo, sourceHandle: null, targetHandle: null });
    setConnectOpen(false);
    setConnectFrom('');
    setConnectTo('');
  };

  return (
    <ReactFlowProvider>
      <div className="cx-root" data-testid="agent-composer-form-page">
        {/* Custom full-bleed chrome — matches the designed Agent Composer workspace (not ShellHeader). */}
        <header className="cx-header" data-testid="agent-composer-header">
          <button
            type="button"
            className="cx-btn cx-btn-ghost"
            onClick={() => navigate(ROUTES.WORKFLOWS)}
            data-testid="back-agent-composer-btn"
          >
            <ArrowLeft size={14} aria-hidden />
            Back to workflows
          </button>

          <div className="cx-header-title" id="composer-heading">
            <span className="cx-kicker">Agent Composer</span>
            <label htmlFor="workflow-name" className="cx-sr-only">
              Workflow name
            </label>
            <input
              id="workflow-name"
              className="cx-header-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              aria-required
              data-testid="agent-composer-name"
            />
          </div>

          <div className="cx-header-actions">
            <button
              type="button"
              className="cx-btn cx-btn-ghost"
              onClick={() => graph.applyAutoLayout()}
              data-testid="agent-composer-layout-btn"
            >
              <LayoutGrid size={14} aria-hidden />
              Layout
            </button>
            <button
              type="button"
              className="cx-btn cx-btn-ghost"
              onClick={() => {
                setConnectFrom(selectedNodeId ?? graph.nodes[0]?.id ?? '');
                setConnectTo('');
                setConnectOpen(true);
              }}
              disabled={graph.nodes.length < 2}
              aria-haspopup="dialog"
              data-testid="agent-composer-connect-btn"
            >
              <Link2 size={14} aria-hidden />
              Connect
            </button>
            <button
              type="button"
              className="cx-btn cx-btn-primary"
              onClick={() => void handleSave()}
              disabled={saving}
              data-testid="save-agent-composer-btn"
            >
              {saving ? (
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Save size={14} aria-hidden />
              )}
              Save
            </button>
          </div>
        </header>

        {validationErrors.length > 0 && (
          <div className="cx-errors" role="alert" data-testid="agent-composer-error-panel">
            <strong>Fix before saving:</strong>
            {validationErrors.map((err) => (
              <div key={err}>• {err}</div>
            ))}
          </div>
        )}

        <main className="cx-body" aria-labelledby="composer-heading">
          <ComposerPalette onAddNode={handleAddNodeAtCenter} />

          <ComposerCanvas
            nodes={nodesWithStatus}
            edges={graph.edges}
            onNodesChange={graph.onNodesChange}
            onEdgesChange={graph.onEdgesChange}
            onConnect={graph.onConnect}
            onAddNode={handleAddNodeAt}
            onSelectionChange={(nodeId, edgeId) => {
              setSelectedNodeId(nodeId);
              setSelectedEdgeId(edgeId);
            }}
            nodeStatuses={stream.nodeStatuses}
          />

          <ComposerInspector
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            nodes={graph.nodes}
            edges={graph.edges}
            onUpdateNode={handleUpdateNode}
            onUpdateEdgeLabel={graph.updateEdgeLabel}
            onDeleteNode={(nodeId) => {
              graph.removeNode(nodeId);
              setSelectedNodeId(null);
            }}
          />
        </main>

        <ComposerRunPanel
          status={stream.status}
          isRunning={stream.isRunning}
          executionId={stream.executionId}
          log={stream.log}
          state={stream.state}
          errorMessage={stream.errorMessage}
          canRun={graph.nodes.length > 0}
          requiresInput={requiresInput}
          onRun={(input) => void handleRun(input)}
          onStop={() => void stream.stop()}
        />

        {connectOpen && (
          <div className="cx-dialog-backdrop" role="presentation" onClick={() => setConnectOpen(false)}>
            <div
              className="cx-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="connect-heading"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="connect-heading">Connect nodes</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label className="block text-sm font-medium mb-1">From</label>
                  <CoordioDropdown
                    options={graph.nodes.map((n) => ({
                      value: n.id,
                      label: `${n.data.label} (${n.id})`,
                    }))}
                    value={connectFrom}
                    onChange={(val) => setConnectFrom(val ?? '')}
                    placeholder="— Select —"
                    size="sm"
                    clearable={false}
                    data-testid="agent-composer-connect-from"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">To</label>
                  <CoordioDropdown
                    options={graph.nodes
                      .filter((n) => n.id !== connectFrom)
                      .map((n) => ({
                        value: n.id,
                        label: `${n.data.label} (${n.id})`,
                      }))}
                    value={connectTo}
                    onChange={(val) => setConnectTo(val ?? '')}
                    placeholder="— Select —"
                    size="sm"
                    clearable={false}
                    data-testid="agent-composer-connect-to"
                  />
                </div>
              </div>
              <div className="cx-dialog-actions">
                <Button variant="outline" onClick={() => setConnectOpen(false)} data-testid="cancel-agent-composer-btn">
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleConnectDialogSubmit}
                  disabled={!connectFrom || !connectTo || connectFrom === connectTo}
                  data-testid="agent-composer-create-connection-btn"
                >
                  Create connection
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ReactFlowProvider>
  );
};
